/**
 * What a modal owes the keyboard and the screen reader, shared by the consent dialog and the app's
 * dialogs: the focusable elements inside it, and the sealing of everything outside it.
 */
const FOCUSABLE =
  "a[href],button:not([disabled]),input:not([disabled]),select:not([disabled])," +
  'textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

/**
 * Everything focusable inside the dialog, in document order.
 *
 * <p>Deliberately not filtered by visibility: the consent dialog's category switches are real inputs
 * kept <c>sr-only</c>, so a layout-based "is it visible" test would drop exactly the controls the trap
 * exists to keep reachable.</p>
 */
export function focusablesIn(root: HTMLElement): HTMLElement[] {
  return [...root.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(
    (el) => !el.hasAttribute("hidden"),
  );
}

/**
 * Makes everything outside the dialog unreachable, and returns the undo.
 *
 * <p><c>inert</c> removes the background from the tab order, from pointer hit-testing and from the
 * accessibility tree; <c>aria-hidden</c> is set alongside it for the assistive technologies that do
 * not implement <c>inert</c> yet. Without this a screen reader would happily read out a page the
 * visitor cannot actually reach.</p>
 *
 * <p>The walk marks the SIBLINGS of the dialog's own top-level branch rather than a single app root:
 * the dialog is portalled to <c>document.body</c>, so the branch to spare is whichever body child
 * contains it, and everything else — the app root, stray widget hosts, a testing container — goes
 * inert. The previous values are captured per element so the undo restores rather than assumes.</p>
 */
export function sealBackground(dialog: HTMLElement): () => void {
  const undo: Array<() => void> = [];

  let branch: Element = dialog;
  while (branch.parentElement && branch.parentElement !== document.body) {
    branch = branch.parentElement;
  }

  for (const el of Array.from(document.body.children)) {
    if (el === branch || el.contains(dialog)) continue;
    const hadInert = el.hasAttribute("inert");
    const hidden = el.getAttribute("aria-hidden");
    el.setAttribute("inert", "");
    el.setAttribute("aria-hidden", "true");
    undo.push(() => {
      if (!hadInert) el.removeAttribute("inert");
      if (hidden === null) el.removeAttribute("aria-hidden");
      else el.setAttribute("aria-hidden", hidden);
    });
  }

  // The overlay covers the viewport, so a scroll behind it would only move a page nobody can touch.
  const overflow = document.body.style.overflow;
  document.body.style.overflow = "hidden";
  undo.push(() => {
    document.body.style.overflow = overflow;
  });

  return () => {
    for (const step of undo) step();
  };
}

/** Keeps Tab inside the dialog: from the last control it wraps to the first, and back. */
export function trapTab(e: KeyboardEvent, dialog: HTMLElement) {
  if (e.key !== "Tab") return;
  const items = focusablesIn(dialog);
  if (items.length === 0) {
    e.preventDefault();
    dialog.focus();
    return;
  }
  const first = items[0];
  const last = items[items.length - 1];
  const active = document.activeElement;
  if (!dialog.contains(active)) {
    e.preventDefault();
    (e.shiftKey ? last : first).focus();
  } else if (e.shiftKey && active === first) {
    e.preventDefault();
    last.focus();
  } else if (!e.shiftKey && active === last) {
    e.preventDefault();
    first.focus();
  }
}
